import { inject, Injectable } from '@angular/core';
import { IChecklistSchedule, IChecklistTimeInterval } from '@fi-shared/checklist/checklist-v2';
import { FiChecklistTimeIntervalType } from '@fi-shared/checklist/schedule/fi-checklist-time-interval-type';
import { FiChecklist2ScheduleIntervalService } from '@fi-shared/checklist/schedule/services/fi-checklist2-schedule-interval.service';
import { FiChecklistScheduleType } from '@fi-shared/checklist/schedule/schedule-edit/fi-checklist-schedule-type';
import { FiContentLanguageSharedService } from '@fi-shared/fi-content-language-shared/fi-content-language-shared.service';
import { AccessRightFilterCollectionModel, PublicationSettingModel } from '@fi-shared/models';
import { cloneDeep } from '@fi-app/shared/utils/clone-deep';

@Injectable()
export class FiChecklist2ScheduleFactoryService {
    private fiContentLanguageSharedService = inject(FiContentLanguageSharedService);
    private fiChecklist2ScheduleIntervalService = inject(FiChecklist2ScheduleIntervalService);

    isRecurringChecklistSchedule(scheduleType: FiChecklistScheduleType): boolean {
        return scheduleType === FiChecklistScheduleType.Recurring;
    }

    getTimeIntervalTypesByScheduleType(scheduleType: FiChecklistScheduleType): FiChecklistTimeIntervalType[] {
        return this.isRecurringChecklistSchedule(scheduleType)
            ? [...this.fiChecklist2ScheduleIntervalService.allRecurringTimeIntervals]
            : [];
    }

    getIntervalTypeByScheduleType(scheduleType: FiChecklistScheduleType): FiChecklistTimeIntervalType {
        switch (scheduleType) {
            case FiChecklistScheduleType.Recurring:
                return FiChecklistTimeIntervalType.RecurringDaily;
            case FiChecklistScheduleType.OneTime:
                return FiChecklistTimeIntervalType.OneTime;
            case FiChecklistScheduleType.OnDemand:
                return FiChecklistTimeIntervalType.OnDemand;
            case FiChecklistScheduleType.Audit:
                return FiChecklistTimeIntervalType.Audit;
        }
    }

    getChecklistScheduleInstance(auditMode: boolean): IChecklistSchedule {
        const timeIntervalType = !auditMode
            ? FiChecklistTimeIntervalType.RecurringDaily
            : FiChecklistTimeIntervalType.Audit;
        const schedule: IChecklistSchedule = {
            Names: [],
            ScheduleType: !auditMode ? FiChecklistScheduleType.Recurring : FiChecklistScheduleType.Audit,
            TimeIntervalType: timeIntervalType,
            TimeIntervals: this.fiChecklist2ScheduleIntervalService.getDefaultTimeIntervals(timeIntervalType),
            HasAnswers: false,
            IsShowReportsToOtherUsers: true,
            AccessRightFilterCollection: AccessRightFilterCollectionModel.getNewInstance(),
            PublicationSetting: PublicationSettingModel.getNewInstance(),
            RemindTime: { Month: 0, Day: 0, Hour: 0, Minute: 0 },
        };
        this.fiContentLanguageSharedService.initializeContentLanguagesArray(schedule.Names);
        return schedule;
    }

    getNewInterval(
        scheduleTimeIntervalKey: string,
        intervalType: FiChecklistTimeIntervalType,
        isEnabled: boolean,
    ): IChecklistTimeInterval {
        const interval: IChecklistTimeInterval = cloneDeep(
            this.fiChecklist2ScheduleIntervalService.getDefaultTimeIntervalByType(
                intervalType,
                +scheduleTimeIntervalKey,
            ),
        );

        interval.IsEnabled = isEnabled;

        if (this.fiChecklist2ScheduleIntervalService.isDailyInterval(intervalType)) {
            interval.Start.DayOfWeek = +scheduleTimeIntervalKey;
            interval.End.DayOfWeek = +scheduleTimeIntervalKey;
        } else if (this.fiChecklist2ScheduleIntervalService.isMonthlyInterval(intervalType)) {
            interval.Start.Month = +scheduleTimeIntervalKey;
            interval.End.Month = +scheduleTimeIntervalKey;
        }

        return interval;
    }
}
